import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  LayoutDashboard,
  FileText,
  Image,
  BarChart3,
  Users,
  Settings,
  Bell,
  Sparkles,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Plus,
  Search,
  FileBarChart,
  Target,
  LayoutTemplate,
  Palette,
  Webhook,
  Radio,
  Workflow,
  FlaskConical,
  BookOpen
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';

const NAV_ITEMS = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: Calendar, label: 'Calendars', path: '/calendars' },
  { icon: FileText, label: 'Posts', path: '/posts' },
  { icon: Sparkles, label: 'Content Ideas', path: '/ideas' },
  { icon: LayoutTemplate, label: 'Templates', path: '/templates' },
  { icon: Palette, label: 'Brand Guidelines', path: '/brand-guidelines' },
  { icon: Image, label: 'Media Library', path: '/media' },
  { icon: BarChart3, label: 'Analytics', path: '/analytics' },
  { icon: Target, label: 'Competitors', path: '/competitors' },
  { icon: FileBarChart, label: 'Reports', path: '/reports' },
  { icon: Webhook, label: 'Webhooks', path: '/webhooks' },
  { icon: Radio, label: 'Social Listening', path: '/listening' },
  { icon: Workflow, label: 'Workflows', path: '/workflows' },
  { icon: FlaskConical, label: 'A/B Testing', path: '/ab-testing' },
  { icon: BookOpen, label: 'User Guide', path: '/guide' },
  { icon: Users, label: 'Team', path: '/team' },
];

const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      navigate(`/posts?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="h-screen flex overflow-hidden bg-slate-50">
      {/* Sidebar - Desktop */}
      <aside
        className={`hidden lg:flex flex-col bg-slate-900 text-slate-300 transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-20'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
          <Link to="/dashboard" className="flex items-center gap-3">
            <img 
              src="/stellisocial-icon-square-transparent.svg" 
              alt="StelliSocial" 
              className="w-10 h-10 rounded-xl flex-shrink-0"
            />
            {sidebarOpen && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="font-bold text-xl text-white tracking-tight"
              >
                Stelli<span className="text-emerald-400">Social</span>
              </motion.span>
            )}
          </Link>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-slate-800 transition-colors"
            data-testid="toggle-sidebar-btn"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>

        {/* Quick Create Button */}
        <div className="p-4" data-tour="create-post">
          <Link to="/posts/new">
            <Button
              className={`w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium ${
                sidebarOpen ? '' : 'px-3'
              }`}
              data-testid="create-post-btn"
            >
              <Plus className="h-5 w-5" />
              {sidebarOpen && <span className="ml-2">Create Post</span>}
            </Button>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto" data-tour="sidebar-nav">
          {NAV_ITEMS.map((item) => {
            const tourId = item.label === 'Calendars' ? 'nav-calendars'
              : item.label === 'Content Ideas' ? 'nav-content-ideas'
              : item.label === 'Analytics' ? 'nav-analytics'
              : item.label === 'User Guide' ? 'nav-user-guide'
              : undefined;
            return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                isActive(item.path)
                  ? 'bg-emerald-500/10 text-emerald-400'
                  : 'hover:bg-slate-800 text-slate-400 hover:text-white'
              }`}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              {...(tourId ? { 'data-tour': tourId } : {})}
            >
              <item.icon className={`h-5 w-5 flex-shrink-0 ${isActive(item.path) ? 'text-emerald-400' : ''}`} />
              {sidebarOpen && (
                <span className="font-medium">{item.label}</span>
              )}
            </Link>
          );
          })}
        </nav>

        {/* Bottom section */}
        <div className="p-3 border-t border-slate-800">
          <Link
            to="/settings"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors hover:bg-slate-800 ${
              isActive('/settings') ? 'bg-emerald-500/10 text-emerald-400' : 'text-slate-400 hover:text-white'
            }`}
            data-testid="nav-settings"
          >
            <Settings className="h-5 w-5" />
            {sidebarOpen && <span className="font-medium">Settings</span>}
          </Link>
        </div>
      </aside>

      {/* Mobile sidebar */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', damping: 20 }}
              className="fixed inset-y-0 left-0 w-72 bg-slate-900 z-50 lg:hidden flex flex-col"
            >
              <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
                <Link to="/dashboard" className="flex items-center gap-3">
                  <img 
                    src="/stellisocial-icon-square-transparent.svg" 
                    alt="StelliSocial" 
                    className="w-10 h-10 rounded-xl"
                  />
                  <span className="font-bold text-xl text-white" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-400">Social</span></span>
                </Link>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 rounded-lg hover:bg-slate-800"
                >
                  <X className="h-5 w-5 text-slate-400" />
                </button>
              </div>

              <div className="p-4">
                <Link to="/posts/new" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg">
                    <Plus className="h-5 w-5 mr-2" />
                    Create Post
                  </Button>
                </Link>
              </div>

              <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto">
                {NAV_ITEMS.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                      isActive(item.path)
                        ? 'bg-emerald-500/10 text-emerald-400'
                        : 'hover:bg-slate-800 text-slate-400 hover:text-white'
                    }`}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                ))}
              </nav>

              <div className="p-3 border-t border-slate-800">
                <Link
                  to="/settings"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white"
                >
                  <Settings className="h-5 w-5" />
                  <span className="font-medium">Settings</span>
                </Link>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top navbar */}
        <header className="h-16 bg-white border-b border-slate-100 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
              data-testid="mobile-menu-toggle"
            >
              <Menu className="h-5 w-5 text-slate-600" />
            </button>

            {/* Search */}
            <div className="hidden md:flex relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="search"
                placeholder="Search posts, calendars..."
                className="w-80 pl-9 h-10 rounded-lg border-slate-200 bg-slate-50 focus:bg-white"
                data-testid="global-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearch}
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Notifications */}
            <Link to="/notifications">
              <Button
                variant="ghost"
                size="icon"
                className="relative"
                data-testid="notifications-btn"
              >
                <Bell className="h-5 w-5 text-slate-600" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
              </Button>
            </Link>

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-100 transition-colors" data-testid="user-menu-btn">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-emerald-100 text-emerald-700">
                      {user?.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-slate-400 hidden md:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5 md:hidden">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator className="md:hidden" />
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                    <Settings className="h-4 w-4" />
                    <span>Settings</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings/billing" className="flex items-center gap-2 cursor-pointer">
                    <BarChart3 className="h-4 w-4" />
                    <span>Billing</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-600 focus:text-red-600 cursor-pointer"
                  data-testid="logout-btn"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
